import axiosInstance from './axiosCreator';
import type { ChatFollowup } from '../types/chatStream';

const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const parseFollowup = (value: unknown): ChatFollowup | null => {
  const raw = record(value);
  if (
    !raw ||
    typeof raw.id !== 'string' ||
    raw.kind !== 'prompt' ||
    typeof raw.label !== 'string' ||
    typeof raw.prompt !== 'string'
  ) {
    return null;
  }
  return {
    id: raw.id,
    kind: 'prompt',
    label: raw.label,
    prompt: raw.prompt,
    // Additive (ORA-827): a chip without it, or with an intent this client does
    // not know, is a perfectly good chip drawn the way chips have always been.
    ...(raw.intent === 'study' ? { intent: 'study' as const } : {}),
  };
};

export interface ChatFollowupsResponse {
  status: 'complete' | 'pending' | 'unavailable';
  items: ChatFollowup[];
}

/**
 * Starts the optional post-turn chip generation. It is deliberately separate
 * from the turn stream: callers never await it before opening another turn.
 */
export const requestChatFollowups = async (
  messageId: string,
  signal: AbortSignal
): Promise<ChatFollowupsResponse | null> => {
  try {
    const response = await axiosInstance.post(
      `/api/v5/chat/messages/${encodeURIComponent(messageId)}/followups`,
      undefined,
      { signal }
    );

    const raw = record(response.data);
    if (!raw) return null;
    const status = raw.status;
    if (
      status !== 'complete' &&
      status !== 'pending' &&
      status !== 'unavailable'
    ) {
      return null;
    }
    return {
      status,
      items: Array.isArray(raw.items)
        ? raw.items
            .map(parseFollowup)
            .filter((item): item is ChatFollowup => item !== null)
        : [],
    };
  } catch {
    // Chips are an optional enhancement. A cancellation, a failed request, or
    // an older backend should never surface as a chat-turn failure.
    return null;
  }
};
